// Raio-X do frete: antes de aceitar uma carga, põe a origem, o destino e o
// valor oferecido e vê o piso da ANTT, o custo da viagem (diesel, despesas da
// viagem, a reserva de manutenção e pneus e o motorista; lib/raiox.js), quanto
// sobra, quanto pedir, o que a empresa já recebeu nessa rota e o Radar de
// retorno (o que sai do destino e da região). Desde a 0.20.0, também no app do
// motorista, igual ao escritório: os números da empresa vêm do servidor
// (raiox_custos), calculados pelo escritório e pela rotina diária. Os
// complementos (cargas menores na mesma rota) somam à oferta.
import { dadosDosRelatorios, raioxDaEmpresa, veiculos, viagens } from '../dados.js';
import { aoChegarDados } from '../local/sincronia.js';
import { creditosDaViagem, regraValendo } from '../lib/acerto.js';
import { EIXOS, TABELAS_DO_PISO, TIPOS_DE_CARGA, VIGENCIA_DO_PISO } from '../lib/antt.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerInteiro, lerNumero, numeroBR, percentual, plural } from '../lib/formato.js';
import { distanciaPeloGoogle, temGoogle } from '../lib/google.js';
import { cidadesDaUf, kmEmLinhaReta, kmEstimado, nomeDaCarga, radarDeRetorno } from '../lib/radar.js';
import { custoDosNumeros, historicoDaRota, mensagemDaContraproposta, numerosDoRaioX, raioX } from '../lib/raiox.js';
import { freteDaRegiao } from '../lib/trajeto.js';
import { linkDeIniciar } from '../lib/modo-viagem.js';
import { UFS } from '../lib/validacao.js';
import { ehAgenciador, ehMotorista, hojeDaEmpresa, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, faixa, formulario, secao } from '../ui/componentes.js';
import { textoDoMomento } from '../lib/entregas.js';

const CHAVE = 'hsafrota-raiox';
function lembrado() {
  try {
    return JSON.parse(localStorage.getItem(CHAVE) ?? '{}') ?? {};
  } catch {
    return {};
  }
}
function lembrar(dados) {
  try {
    localStorage.setItem(CHAVE, JSON.stringify({ ...lembrado(), ...dados }));
  } catch {
    // Sem armazenamento (aba anônima): só não lembra.
  }
}

// Quanto quer que sobre, para começar (os custos fixos e o lucro saem daí).
const SOBRA_PADRAO = 30;
const kmPorLitro = (v) => `${numeroBR(v, { casas: 2 })} km/l`;
export const tratoresAtivos = (lista) => lista.filter((v) => v.tipo === 'trator' && v.ativo && !v.deleted_at);

// O Planejar o trajeto (0.22.0) do app de quem está usando, com a origem e o
// caminhão já escolhidos (do caminhão vazio, por exemplo).
export function linkDoTrajeto({ origem = null, uf = null, caminhao = null } = {}) {
  const base = ehAgenciador() ? '/a/trajeto' : ehMotorista() ? '/m/trajeto' : '/trajeto';
  const busca = new URLSearchParams();
  if (origem && uf) {
    busca.set('origem', origem);
    busca.set('uf', uf);
  }
  if (caminhao) busca.set('caminhao', caminhao);
  const texto = busca.toString();
  return `#${base}${texto ? `?${texto}` : ''}`;
}

// ---------------------------------------------------------------------
// De onde vêm os números
// ---------------------------------------------------------------------
// Escritório: as tabelas do aparelho (refeitas na hora da conta, porque os
// dados podem chegar depois de a tela abrir). Por trás, guarda os números e as
// preferências no servidor, para o app do motorista.
export async function fonteDoEscritorio() {
  const hoje = hojeDaEmpresa();
  const calcular = (tabelas) => numerosDoRaioX(tabelas, { hoje, regraValendo, creditosDaViagem });
  // O servidor primeiro: os dados do aparelho são lidos por último, logo antes
  // de a tela passar a ouvir os dados que chegam (senão os que chegassem no
  // meio ficariam fora das dicas).
  const servidor = await raioxDaEmpresa.lerSeDer();
  const dados = await dadosDosRelatorios();
  let guardadas = servidor ? { reserva: numeroOuNulo(servidor.reserva_km), sobra: numeroOuNulo(servidor.sobra_pct) } : null;
  // O frete de retorno por região (Planejar o trajeto): o guardado ou o padrão.
  let freteRegiao = freteDaRegiao(servidor?.frete_regiao);
  return {
    tratores: tratoresAtivos(dados.veiculos),
    caminhaoPadrao: null,
    numerosIniciais: calcular(dados),
    numerosDeNovo: async () => calcular(await dadosDosRelatorios()),
    numeros: async () => calcular(await dadosDosRelatorios()),
    historico: async (rota) => historicoDaRota((await dadosDosRelatorios()).fretes, rota),
    preferencias: { reserva: guardadas?.reserva ?? null, sobra: guardadas?.sobra ?? null },
    aoCalcular: (numeros, { reserva, sobra }) => {
      if (!podeGravar()) return;
      raioxDaEmpresa.guardarNumeros(numeros);
      if (guardadas?.reserva !== reserva || guardadas?.sobra !== sobra) {
        guardadas = { reserva, sobra };
        raioxDaEmpresa.guardarPreferencias(reserva, sobra);
      }
    },
    dicaDaReserva: 'Quanto você separa por km rodado para manutenção e pneus. Vale para todos os caminhões e para o app do motorista.',
    freteRegiao,
    guardarFreteRegiao: (novo) => {
      if (!podeGravar() || JSON.stringify(novo) === JSON.stringify(freteRegiao)) return;
      freteRegiao = novo;
      raioxDaEmpresa.guardarFreteRegiao(novo);
    },
  };
}

// App do motorista e do agenciador: os números que o escritório e a rotina
// diária guardaram no servidor (precisa de internet, como o radar). O
// caminhão do motorista é o da viagem dele em andamento.
export async function fonteDoServidor() {
  const [frota, lista, linha] = await Promise.all([veiculos.listar(), viagens.listar(), raioxDaEmpresa.ler()]);
  const aberta = ehAgenciador() ? null : lista.find((v) => v.status === 'aberta');
  return {
    tratores: tratoresAtivos(frota),
    caminhaoPadrao: aberta?.veiculo_id ?? null,
    numerosIniciais: linha?.numeros ?? null,
    numeros: async () => linha?.numeros ?? null,
    historico: (rota) => raioxDaEmpresa.historico(rota).catch((erro) => {
      console.error('Raio-X: histórico da rota:', erro);
      return { erro: true };
    }),
    preferencias: { reserva: numeroOuNulo(linha?.reserva_km), sobra: numeroOuNulo(linha?.sobra_pct) },
    freteRegiao: freteDaRegiao(linha?.frete_regiao),
    aoCalcular: () => {},
    calculadoEm: linha?.calculado_em ?? null,
    dicaDaReserva: linha?.reserva_km != null
      ? 'A reserva que a empresa usa por km rodado. Mudar aqui serve só para esta conta.'
      : 'A empresa ainda não definiu a reserva por km para manutenção e pneus: digite um valor para fazer a conta.',
  };
}

export const numeroOuNulo = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// ---------------------------------------------------------------------
// Telas
// ---------------------------------------------------------------------
export function telaRaioX({ busca } = {}) {
  return telaComFonte({ fonte: fonteDoEscritorio, voltar: '/mais', busca, atualizaSozinho: true });
}

export function telaRaioXDeFora({ busca } = {}) {
  return telaComFonte({ fonte: fonteDoServidor, voltar: ehAgenciador() ? '/a' : '/m', busca, atualizaSozinho: false });
}

// `?origem=Laguna&uf=SC` (do caminhão vazio): o radar dessa cidade em cima e
// a origem já preenchida; `&caminhao=<id>`: esse caminhão já escolhido.
function telaComFonte({ fonte, voltar, busca, atualizaSozinho }) {
  // O formulário não é redesenhado quando chegam dados: as contas são feitas ao tocar no botão.
  const corpo = h('div', { class: 'secao', 'data-atualiza-sozinha': '' }, carregando());
  const origem = busca?.get?.('origem') || null;
  const uf = busca?.get?.('uf') || null;
  const caminhao = busca?.get?.('caminhao') || null;
  (async () => {
    const f = await fonte();
    if (caminhao) f.caminhaoPadrao = caminhao;
    const e = sessao.empresa;
    const lugarDoRadar = origem && uf ? h('div', null, carregando('Buscando o que sai de lá…')) : null;
    trocar(corpo,
      lugarDoRadar && secao(`O que sai de ${origem}/${uf}`, lugarDoRadar),
      h('a', { class: 'botao secundario cheio', href: linkDoTrajeto({ origem, uf, caminhao }) }, 'Planejar o trajeto'),
      ...montar({ fonte: f, origem, uf, atualizaSozinho }));
    if (lugarDoRadar) {
      blocoDoRadar({ cidade: origem, uf, ufCasa: e?.uf ?? null, cidadeCasa: e?.cidade ?? null })
        .then((bloco) => trocar(lugarDoRadar, bloco))
        .catch((erro) => trocar(lugarDoRadar, faixa(mensagemDeErro(erro), 'alerta', 'O radar não abriu agora')));
    }
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo', 'Não deu para abrir o Raio-X'));
  });
  return casca({ titulo: 'Raio-X do frete', voltar, conteudo: [corpo] });
}

// As dicas dos campos do custo, com o que o app usa quando ficam em branco.
function dicaDoConsumo(numeros, caminhao) {
  const doCaminhao = caminhao ? numeros?.caminhoes?.[caminhao]?.consumo : null;
  if (doCaminhao) return `Em branco, o app usa a média deste caminhão nos últimos 6 meses: ${kmPorLitro(doCaminhao.kmPorLitro)}.`;
  const daFrota = numeros?.frota?.consumo;
  if (daFrota) return `Em branco, o app usa a média da frota nos últimos 6 meses: ${kmPorLitro(daFrota.kmPorLitro)}.`;
  return 'Ainda sem abastecimentos com o km do painel: digite o consumo médio do caminhão.';
}

function dicaDoDiesel(numeros) {
  const p = numeros?.diesel ?? { preco: VIGENCIA_DO_PISO.diesel, fonte: 'antt' };
  if (p.fonte === 'recentes') return `Em branco, o app usa a média paga nos últimos 30 dias: ${dinheiro(p.preco)}.`;
  if (p.fonte === 'ultimos') return `Em branco, o app usa a média dos últimos abastecimentos: ${dinheiro(p.preco)}.`;
  return `Em branco, o app usa o preço da tabela da ANTT: ${dinheiro(p.preco)}.`;
}

function dicaDaReserva(numeros, base) {
  const m = numeros?.despesas?.manutencao;
  return m != null ? `${base} A frota gastou ${dinheiro(m)} por km com manutenção e pneus nos últimos 12 meses.` : base;
}

// Os complementos: cargas menores pegas na mesma rota (o valor e o desvio).
function editorDeComplementos() {
  let n = 0;
  const lista = h('div', { class: 'lista complementos' });
  const adicionar = () => {
    n += 1;
    const bloco = h('div', { class: 'cartao complemento' }, h('div', { class: 'cartao-corpo' },
      h('div', { class: 'grade-2' },
        campo({ rotulo: 'Valor do complemento (R$)', nome: `complemento_valor_${n}`, formato: 'dinheiro', atributos: { inputmode: 'decimal' } }),
        campo({ rotulo: 'Km a mais no caminho', nome: `complemento_km_${n}`, formato: 'km', atributos: { inputmode: 'numeric' } })),
      h('button', { class: 'botao texto', type: 'button', onClick: () => bloco.remove() }, 'Tirar o complemento')));
    lista.append(bloco);
    bloco.querySelector('input').focus();
  };
  return {
    elemento: h('fieldset', { class: 'campo complementos-do-raiox' },
      h('legend', null, 'Complementos (opcional)'),
      h('p', { class: 'dica' }, 'Carga menor pega na mesma rota para completar o frete. O valor soma à oferta; os km a mais (desvio) entram no custo.'),
      lista,
      h('button', { class: 'botao secundario', type: 'button', onClick: adicionar }, '+ Complemento')),
    ler: (d) => Object.keys(d).filter((k) => k.startsWith('complemento_valor_')).map((k) => ({
      campo: k, valor: lerNumero(d[k]), kmExtra: lerInteiro(d[k.replace('_valor_', '_km_')]) ?? 0,
    })),
  };
}

function montar({ fonte, origem: origemDoRadar, uf: ufDoRadar, atualizaSozinho }) {
  const memoria = lembrado();
  const e = sessao.empresa;
  const { tratores } = fonte;
  const resultado = h('div', { class: 'lista', 'aria-live': 'polite' });
  let desligarDados = null;
  let numerosDasDicas = fonte.numerosIniciais;
  const listaOrigem = h('datalist', { id: 'raiox-cidades-origem' });
  const listaDestino = h('datalist', { id: 'raiox-cidades-destino' });
  const opcoesDeUf = [['', 'UF'], ...UFS.map((u) => [u, u])];

  const preencherCidades = (lista, uf) => {
    cidadesDaUf(uf).then((nomes) => trocar(lista, ...nomes.map((n) => h('option', { value: n })))).catch(() => {});
  };

  const existe = (id) => id && tratores.some((t) => t.id === id);
  const caminhaoPadrao = existe(fonte.caminhaoPadrao) ? fonte.caminhaoPadrao : existe(memoria.caminhao) ? memoria.caminhao : tratores[0]?.id ?? '';
  // Os eixos do cadastro do caminhão (0.22.0) ou os lembrados dele.
  const eixosPadrao = (id) => String(tratores.find((t) => t.id === id)?.eixos ?? memoria.eixos?.[id] ?? 5);
  const reservaInicial = fonte.preferencias.reserva ?? memoria.reserva ?? null;
  const sobraInicial = fonte.preferencias.sobra ?? memoria.sobra ?? SOBRA_PADRAO;
  const origemInicial = origemDoRadar ?? memoria.origem ?? '';
  const ufInicial = ufDoRadar ?? memoria.uf_origem ?? e?.uf ?? '';
  // Sem consumo nenhum (empresa nova), o campo do consumo já aparece aberto.
  const semConsumo = !numerosDasDicas?.frota?.consumo;
  const complementos = editorDeComplementos();

  const form = formulario({
    filhos: [
      h('p', { class: 'pequeno' }, 'Antes de aceitar uma carga, veja o piso da ANTT, o custo da viagem, quanto sobra e quanto pedir.'),
      h('fieldset', { class: 'campo' },
        h('legend', null, 'De onde sai'),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Cidade', nome: 'origem', obrigatorio: true, formato: 'nome', valor: origemInicial, atributos: { list: 'raiox-cidades-origem', autocomplete: 'off' } }),
          campo({ rotulo: 'UF', nome: 'uf_origem', tipo: 'select', obrigatorio: true, valor: ufInicial, opcoes: opcoesDeUf,
            aoMudar: (ev) => preencherCidades(listaOrigem, ev.target.value) })),
        listaOrigem),
      h('fieldset', { class: 'campo' },
        h('legend', null, 'Para onde vai'),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Cidade', nome: 'destino', obrigatorio: true, formato: 'nome', atributos: { list: 'raiox-cidades-destino', autocomplete: 'off' } }),
          campo({ rotulo: 'UF', nome: 'uf_destino', tipo: 'select', obrigatorio: true, valor: '', opcoes: opcoesDeUf,
            aoMudar: (ev) => preencherCidades(listaDestino, ev.target.value) })),
        listaDestino),
      campo({ rotulo: 'Valor oferecido (R$)', nome: 'valor', obrigatorio: true, formato: 'dinheiro' }),
      campo({ rotulo: 'Km carregado', nome: 'km', formato: 'km', dica: 'Em branco, o app calcula.', atributos: { inputmode: 'numeric' } }),
      tratores.length > 0 && campo({
        rotulo: 'Caminhão', nome: 'caminhao', tipo: 'select', valor: caminhaoPadrao,
        opcoes: tratores.map((t) => [t.id, [t.placa, t.apelido].filter(Boolean).join(' · ')]),
      }),
      campo({ rotulo: 'Eixos carregados (caminhão + carreta)', nome: 'eixos', tipo: 'select', valor: eixosPadrao(caminhaoPadrao),
        opcoes: EIXOS.map((n) => [String(n), `${n} eixos`]) }),
      campo({ rotulo: 'Tipo de carga', nome: 'tipo', tipo: 'select', valor: memoria.tipo ?? 'carga_geral', opcoes: TIPOS_DE_CARGA }),
      campo({ rotulo: 'Volta', nome: 'volta', tipo: 'segmentos', valor: 'vazia',
        opcoes: [['vazia', 'Volta vazia', 'conta a volta no custo'], ['carregada', 'Tenho carga de volta', 'conta só a ida']] }),
      complementos.elemento,
      campo({ rotulo: 'Reserva para manutenção e pneus (R$ por km)', nome: 'reserva', obrigatorio: true, formato: 'dinheiro',
        valor: reservaInicial !== null && reservaInicial !== undefined ? numeroBR(reservaInicial, { casas: 2 }) : '',
        dica: dicaDaReserva(numerosDasDicas, fonte.dicaDaReserva), atributos: { inputmode: 'decimal' } }),
      campo({ rotulo: 'Quanto quer que sobre (%)', nome: 'margem', formato: 'percentual', valor: numeroBR(sobraInicial),
        dica: 'Para os custos fixos (seguro, IPVA, parcelas…) e o lucro. O app sugere o valor para pedir com essa sobra (e nunca abaixo do piso).',
        atributos: { inputmode: 'decimal' } }),
      h('details', { class: 'mais-detalhes', open: semConsumo },
        h('summary', null, 'Consumo e preço do diesel'),
        campo({ rotulo: 'Consumo (km/l)', nome: 'consumo', formato: 'percentual', dica: dicaDoConsumo(numerosDasDicas, caminhaoPadrao),
          atributos: { inputmode: 'decimal' } }),
        campo({ rotulo: 'Preço do diesel (R$ por litro)', nome: 'diesel', formato: 'dinheiro', dica: dicaDoDiesel(numerosDasDicas),
          atributos: { inputmode: 'decimal' } })),
      h('details', { class: 'mais-detalhes' },
        h('summary', null, 'Tabela do piso da ANTT'),
        campo({ rotulo: 'Tabela', nome: 'tabela', tipo: 'select', valor: 'A', opcoes: TABELAS_DO_PISO }),
        h('p', { class: 'pequeno' }, `${VIGENCIA_DO_PISO.ato}, em vigor desde ${data(VIGENCIA_DO_PISO.desde)} (diesel a ${dinheiro(VIGENCIA_DO_PISO.diesel)}).`)),
    ],
    textoBotao: 'Fazer o Raio-X',
    textoEnviando: 'Calculando…',
    validar: (d) => {
      const erros = {};
      if (!(lerNumero(d.valor) > 0)) erros.valor = 'Informe o valor oferecido.';
      const reserva = lerNumero(d.reserva);
      if (reserva === null) erros.reserva = 'Informe quanto separa por km para manutenção e pneus (pode ser 0).';
      else if (!(reserva >= 0 && reserva <= 20)) erros.reserva = 'Confira o valor: é por km rodado (ex.: 0,80).';
      const consumo = lerNumero(d.consumo);
      if (consumo !== null && !(consumo > 0 && consumo <= 10)) erros.consumo = 'Confira o consumo: quantos km o caminhão faz com 1 litro.';
      const diesel = lerNumero(d.diesel);
      if (diesel !== null && !(diesel >= 1 && diesel <= 20)) erros.diesel = 'Confira o preço do litro.';
      const margem = lerNumero(d.margem);
      if (margem !== null && !(margem >= 0 && margem < 95)) erros.margem = 'Use um número de 0 a 94.';
      for (const c of complementos.ler(d)) {
        if (!(c.valor > 0)) erros[c.campo] = 'Informe o valor do complemento (ou toque em "Tirar o complemento").';
      }
      return erros;
    },
    aoEnviar: async (d) => {
      const tabela = d.tabela || 'A';
      const margem = (lerNumero(d.margem) ?? SOBRA_PADRAO) / 100;
      const reserva = lerNumero(d.reserva);
      lembrar({
        origem: d.origem, uf_origem: d.uf_origem, tipo: d.tipo, sobra: margem * 100, reserva, caminhao: d.caminhao || null,
        eixos: { ...(lembrado().eixos ?? {}), [d.caminhao || 'frota']: Number(d.eixos) },
      });
      const rota = { origem: `${d.origem}/${d.uf_origem}`, destino: `${d.destino}/${d.uf_destino}` };
      const daRota = { origemCidade: d.origem, origemUf: d.uf_origem, destinoCidade: d.destino, destinoUf: d.uf_destino };
      const km = lerNumero(d.km) || (await calcularKm(d));
      if (!km) {
        form.querySelector('[name="km"]')?.focus();
        throw new Error('KM: não deu para calcular a distância. Digite os km carregado.');
      }
      const listaDeComplementos = complementos.ler(d);
      // As contas (no escritório, refeitas quando chegam dados do servidor).
      const contas = async () => {
        const numeros = await fonte.numeros();
        const custo = custoDosNumeros(numeros, {
          veiculoId: d.caminhao || null, reserva, consumo: lerNumero(d.consumo), diesel: lerNumero(d.diesel),
        });
        const r = raioX({
          valor: lerNumero(d.valor), km: km.km ?? km, voltaVazia: d.volta !== 'carregada', custo, tabela, tipo: d.tipo, eixos: Number(d.eixos), margem,
          complementos: listaDeComplementos,
        });
        const historico = await fonte.historico(daRota);
        fonte.aoCalcular(numeros, { reserva, sobra: margem * 100 });
        return blocosDoResultado({ r, km, custo, historico, rota, d, calculadoEm: fonte.calculadoEm, complementos: listaDeComplementos });
      };
      const numeros = h('div', { class: 'so-conteudo' }, ...(await contas()));
      trocar(resultado, numeros);
      resultado.scrollIntoView({ block: 'start', behavior: 'smooth' });
      desligarDados?.();
      desligarDados = atualizaSozinho ? aoChegarDados(async () => {
        if (!numeros.isConnected) {
          desligarDados?.();
          desligarDados = null;
          return;
        }
        const blocos = await contas();
        if (numeros.isConnected) trocar(numeros, ...blocos);
      }) : null;
      // O radar vem da internet, depois.
      const lugarDoRadar = h('div', null, carregando('Buscando o que sai de lá…'));
      resultado.append(secao('Radar de retorno', lugarDoRadar));
      blocoDoRadar({ cidade: d.destino, uf: d.uf_destino, ufCasa: e?.uf ?? null, cidadeCasa: e?.cidade ?? null })
        .then((bloco) => trocar(lugarDoRadar, bloco))
        .catch((erro) => trocar(lugarDoRadar, faixa(mensagemDeErro(erro), 'alerta', 'O radar não abriu agora')));
    },
  });

  // Lista de cidades da UF já escolhida (origem lembrada)
  if (ufInicial) preencherCidades(listaOrigem, ufInicial);
  // As dicas mostram o que o app usa quando o campo fica em branco: refeitas
  // ao trocar de caminhão e (no escritório) quando chegam dados.
  const trocarDica = (nome, texto) => {
    const dica = form.querySelector(`[data-campo="${nome}"] .dica`);
    if (dica) dica.textContent = texto;
  };
  const refazerDicas = () => {
    const caminhao = form.querySelector('[name="caminhao"]')?.value ?? '';
    trocarDica('reserva', dicaDaReserva(numerosDasDicas, fonte.dicaDaReserva));
    trocarDica('consumo', dicaDoConsumo(numerosDasDicas, caminhao));
    trocarDica('diesel', dicaDoDiesel(numerosDasDicas));
  };
  if (fonte.numerosDeNovo) {
    const desligarDicas = aoChegarDados(async () => {
      if (!form.isConnected) {
        desligarDicas();
        return;
      }
      try {
        numerosDasDicas = await fonte.numerosDeNovo();
        if (form.isConnected) refazerDicas();
      } catch (erro) {
        console.warn(`Raio-X: dicas: ${erro?.message ?? erro}`);
      }
    });
  }
  // Ao trocar de caminhão, os eixos lembrados dele e o consumo dele
  form.querySelector('[name="caminhao"]')?.addEventListener('change', (ev) => {
    const eixos = form.querySelector('[name="eixos"]');
    if (eixos) eixos.value = eixosPadrao(ev.target.value);
    refazerDicas();
  });

  return [form, resultado];
}

// A distância: pelo Google, se der; senão, a estimativa pela linha reta.
async function calcularKm(d) {
  if (temGoogle()) {
    try {
      const km = await distanciaPeloGoogle(`${d.origem}, ${d.uf_origem}, Brasil`, `${d.destino}, ${d.uf_destino}, Brasil`);
      if (km) return { km: Math.round(km), fonte: 'google' };
    } catch (erro) {
      console.warn(`Raio-X: distância pelo Google: ${erro?.message ?? erro}`);
    }
  }
  const reta = await kmEmLinhaReta({ origem: d.origem, ufOrigem: d.uf_origem, destino: d.destino, ufDestino: d.uf_destino }).catch(() => null);
  const km = kmEstimado(reta?.linha_reta);
  return km ? { km, fonte: 'estimativa' } : null;
}

function veredito(r) {
  if (r.abaixoDoPiso) return faixa(`A oferta está abaixo do piso mínimo da ANTT (${dinheiro(r.piso)}). A lei não deixa contratar abaixo do piso.`, 'perigo', 'Abaixo do piso');
  if (r.sobra !== null && r.sobra < 0) return faixa(`Pelo custo da viagem, essa carga dá prejuízo de ${dinheiro(-r.sobra)}.`, 'perigo', 'Dá prejuízo');
  if (r.sugerido !== null && r.valor < r.sugerido) {
    return faixa(`Sobra pouco: para sobrar o que você quer, peça ${dinheiro(r.sugerido)}${r.complementos > 0 ? ' pela carga principal' : ''}.`, 'alerta', 'Dá para pedir mais');
  }
  if (r.custo === null) return faixa('A oferta está acima do piso da ANTT.', 'ok', 'Acima do piso');
  return faixa('A oferta cobre o custo da viagem, a sobra que você quer e o piso da ANTT.', 'ok', 'Vale a pena');
}

// De onde veio cada número do custo, uma linha para cada.
function deOndeVeio(custo, calculadoEm = null) {
  const partes = [];
  const c = custo.consumo;
  const consumo = c.fonte === 'digitado' ? `${kmPorLitro(c.kmPorLitro)} (o que você digitou)`
    : c.fonte === 'caminhao' ? `${kmPorLitro(c.kmPorLitro)} (a média deste caminhão nos últimos 6 meses)`
      : `${kmPorLitro(c.kmPorLitro)} (a média da frota nos últimos 6 meses)`;
  const p = custo.diesel;
  const preco = p.fonte === 'digitado' ? `${dinheiro(p.preco)} o litro (o que você digitou)`
    : p.fonte === 'recentes' ? `${dinheiro(p.preco)} o litro (a média paga nos últimos 30 dias)`
      : p.fonte === 'ultimos' ? `${dinheiro(p.preco)} o litro (a média dos últimos abastecimentos)`
        : `${dinheiro(p.preco)} o litro (o preço da tabela da ANTT)`;
  partes.push(`Diesel: ${consumo}, a ${preco}.`);
  partes.push(custo.despesas
    ? `Despesas da viagem: ${dinheiro(custo.despesas.viagem)} por km, a média da frota nos últimos 12 meses (pedágio, Arla, alimentação, pernoite, chapa, balança e estacionamento).`
    : 'Despesas da viagem: ainda sem km lançados para fazer a média (lance o km do painel no diesel).');
  partes.push(`Reserva de manutenção e pneus: ${dinheiro(custo.reserva)} por km.`);
  const m = custo.motorista;
  partes.push(m.fonte === 'caminhao' ? `Motorista e agenciamento: ${percentual(m.pct * 100)} do frete, pelo que este caminhão pagou nos últimos meses.`
    : m.fonte === 'frota' ? `Motorista e agenciamento: ${percentual(m.pct * 100)} do frete, pelo que a frota pagou nos últimos meses.`
      : 'Motorista e agenciamento: ainda sem cargas nos últimos meses para fazer a conta.');
  partes.push('Os custos fixos (seguro, IPVA, parcelas, rastreador) e as despesas gerais não entram: saem da sobra.');
  if (calculadoEm) partes.push(`Números da empresa calculados em ${textoDoMomento(calculadoEm, { fuso: sessao.empresa?.fuso_horario })}.`);
  return partes;
}

function blocosDoResultado({ r, km, custo, historico, rota, d, calculadoEm = null, complementos = [] }) {
  const kmTexto = typeof km === 'object' && km.fonte === 'estimativa'
    ? `${numeroBR(r.km)} km (estimativa: a linha reta + 25%; confira)`
    : typeof km === 'object' && km.fonte === 'google' ? `${numeroBR(r.km)} km (pelo Google)` : `${numeroBR(r.km)} km`;
  const porKm = r.porKm ? ` (${dinheiro(r.porKm)} por km)` : '';
  const linhas = [
    ['Oferta', r.complementos > 0
      ? `${dinheiro(r.valor)} + ${dinheiro(r.complementos)} de complemento = ${dinheiro(r.total)}${porKm}`
      : `${dinheiro(r.valor)}${porKm}`],
    ['Distância', r.kmExtra > 0 ? `${kmTexto} + ${numeroBR(r.kmExtra)} km de desvio` : kmTexto],
    ['Piso da ANTT', r.piso !== null ? dinheiro(r.piso) : 'sem piso para esse tipo de carga com esses eixos'],
  ];
  if (r.custo !== null) {
    linhas.push(['Diesel', dinheiro(r.partes.diesel)]);
    linhas.push(['Despesas da viagem', dinheiro(r.partes.viagem)]);
    linhas.push(['Manutenção e pneus (reserva)', dinheiro(r.partes.reserva)]);
    linhas.push(['Motorista e agenciamento', dinheiro(r.partes.motorista)]);
    linhas.push(['Custo da viagem', `${dinheiro(r.custo)} (${numeroBR(r.kmRodados)} km${d.volta !== 'carregada' ? ', com a volta vazia' : ''})`]);
    linhas.push(['Sobra', `${dinheiro(r.sobra)}${r.margem !== null ? ` (${percentual(Math.round(r.margem * 1000) / 10)})` : ''}`]);
  }
  const pedirMais = r.sugerido !== null && r.valor < r.sugerido;
  const mensagem = pedirMais ? mensagemDaContraproposta({ ...rota, km: r.km, valor: r.sugerido, piso: r.piso }) : null;
  // Modo viagem (0.23.0): no escritório, a carga aceita vira a viagem, com este previsto.
  const podeAceitar = podeGravar() && !ehMotorista() && !ehAgenciador();
  const aceitar = podeAceitar && (r.abaixoDoPiso
    ? h('p', { class: 'pequeno' }, 'Para aceitar e iniciar a viagem, a oferta precisa estar no piso da ANTT ou acima.')
    : h('a', { class: 'botao cheio', href: linkDeIniciar({ d, km: r.km, complementos }) }, 'Aceitar e iniciar a viagem'));
  return [
    veredito(r),
    h('dl', { class: 'linha-dados' }, linhas.flatMap(([t, v]) => [h('dt', null, t), h('dd', null, v)])),
    aceitar,
    r.custo === null
      ? h('p', { class: 'pequeno' }, 'Sem o consumo do caminhão ainda: digite o consumo em "Consumo e preço do diesel", lá em cima, ou lance os abastecimentos com o km do painel. Aí o Raio-X mostra o custo da viagem e quanto sobra.')
      : h('ul', { class: 'lista-simples pequeno' }, deOndeVeio(custo, calculadoEm).map((t) => h('li', null, t))),
    pedirMais && h('div', { class: 'faixa info' }, h('div', null,
      h('strong', null, `Peça ${dinheiro(r.sugerido)}`),
      r.complementos > 0 ? ' pela carga principal' : '',
      r.motivoDoSugerido === 'piso' ? ' (o piso da ANTT, arredondado).' : ` (com a sobra que você quer${r.complementos > 0 ? ', contando os complementos' : ''}${r.piso ? ' e acima do piso' : ''}).`)),
    historico?.erro
      ? h('p', { class: 'pequeno' }, 'O histórico da rota não abriu agora (confira a internet).')
      : historico
        ? h('p', null, `Nessa rota, a empresa já fez ${plural(historico.cargas, 'carga', 'cargas')}: média de ${dinheiro(historico.media)}`
          + ` (de ${dinheiro(historico.menor)} a ${dinheiro(historico.maior)}). A última, em ${data(historico.ultima.data)}, por ${dinheiro(historico.ultima.valor)}.`)
        : h('p', { class: 'pequeno' }, 'A empresa ainda não fez cargas nessa rota.'),
    mensagem && h('div', { class: 'acoes' },
      h('button', {
        class: 'botao cheio',
        type: 'button',
        onClick: async () => {
          try {
            await navigator.clipboard.writeText(mensagem);
            avisar('Mensagem copiada. É só colar no WhatsApp.');
          } catch {
            avisar('Não deu para copiar. Use "Abrir no WhatsApp".', 'erro');
          }
        },
      }, 'Copiar a contraproposta'),
      h('a', { class: 'botao cheio secundario', href: `https://wa.me/?text=${encodeURIComponent(mensagem)}`, target: '_blank', rel: 'noopener' }, 'Abrir no WhatsApp')),
  ];
}

async function blocoDoRadar({ cidade, uf, ufCasa, cidadeCasa }) {
  const radar = await radarDeRetorno({ cidade, uf, raioKm: 50, ufCasa });
  if (!radar?.pronto) return h('p', { class: 'pequeno' }, 'O radar ainda está sendo montado: os dados da ANTT chegam uma vez por mês.');
  if (!radar.cidade) return h('p', { class: 'pequeno' }, `Não achei ${cidade}/${uf} na lista do IBGE. Confira o nome da cidade.`);
  const meses = `${data(radar.info.mes_inicio).slice(3)} a ${data(radar.info.mes_fim).slice(3)}`;
  const porMes = (n) => `${numeroBR(Math.round(n))} por mês`;
  return h('div', { class: 'lista' },
    h('p', null, `De ${radar.cidade.nome}/${radar.cidade.uf} e das cidades a até ${radar.raio_km} km (${plural(radar.municipios, 'cidade', 'cidades')}) saem umas `,
      h('strong', null, `${numeroBR(Math.round(radar.total_mes))} cargas por mês`), '.',
      radar.uf_casa && radar.para_casa !== null ? [' Para ', h('strong', null, radar.uf_casa), `${cidadeCasa ? ` (sua base é ${cidadeCasa})` : ''}: `, h('strong', null, porMes(radar.para_casa)), '.'] : null),
    radar.cargas.length > 0 && h('div', null, h('h3', null, 'O que mais sai'),
      h('ul', null, radar.cargas.map((c) => h('li', null, `${nomeDaCarga(c.ncm, c.descricao)}: ${porMes(c.ciots_mes)}`)))),
    radar.destinos.length > 0 && h('div', null, h('h3', null, 'Para onde vai'),
      h('ul', null, radar.destinos.map((x) => h('li', null, `${x.nome}/${x.uf}: ${porMes(x.ciots_mes)}`)))),
    h('p', { class: 'pequeno' }, `Média de ${meses}, pelos CIOTs registrados na ANTT (dados abertos). Mostra quanta carga sai, não o preço.`));
}
